import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import { MessageDetail, statusBadge } from './cop-adatp3-shared.jsx';
import {
  LastRefresh, ExportButtons, SearchInput, applySearch,
  useTableState, SortIcon, Pagination,
} from '../../lib/table-utils.jsx';

/**
 * ADatP-3 Bridge — admin page. Port of reference
 * `manuals/COP-microservice-integration/ADatP-3 Bridge/adatp3-page.jsx`.
 *
 * Scope: messages that produced map-visible geometry (the source of
 * cop-l-adatp3-* layers). Text-only messages live on ADatP-3 Orders.
 *
 * Adjustments vs reference — same set documented in cop-locations-page.jsx
 * (timezone prop, useToast, api.cop.* paths, shadow-card, PageShell).
 */

const POLL_MS = 5000;
const PAGE_SIZE = 25;

function hasGeometry(m) {
  return Boolean(m?.hasGeometry) || (m?.geometryCount || 0) > 0;
}

export function CopAdatp3BridgePage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const isAdmin = currentUser?.role === 'admin';
  const formatDate = useCallback((iso) => iso ? formatUtcInTimezone(iso, tz) : '—', [tz]);

  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', originator: '', parseStatus: '' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showRouting, setShowRouting] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [acksSummary, setAcksSummary] = useState({});
  const [notInstalled, setNotInstalled] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        api.cop.adatp3.status(),
        api.cop.adatp3.messages(filter),
      ]);
      setStatus(s);
      setMessages((m.messages || []).filter(hasGeometry));
      setRefreshedAt(new Date());
      setNotInstalled(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setNotInstalled(err.payload || { reason: 'unknown' });
      } else {
        toast.error(`ADatP-3 reload failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
    try {
      const sum = await api.cop.adatp3.acksSummary();
      setAcksSummary(sum.summary || {});
    } catch (err) {
      if (err.status !== 503 && err.status !== 404) {
        // eslint-disable-next-line no-console
        console.warn('[Adatp3] acks summary failed:', err.message);
      }
    }
  }, [filter, toast]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  const openDetail = useCallback(async (key) => {
    setSelected(key);
    setDetail(null);
    try {
      const data = await api.cop.adatp3.message(key);
      setDetail(data);
    } catch (err) {
      toast.error(`Load failed: ${err.message}`);
    }
  }, [toast]);

  const handleDeleteOne = useCallback(async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(`Delete message ${m.key}?\n\nThis removes the message from the inbox AND any map objects derived from it (cop-l-adatp3-* layers). The dataset itself is preserved; bridge will auto-create new objects on next inbound message of the same type.`)) return;
    try {
      await api.cop.adatp3.remove(m.key);
      toast.success(`Deleted ${m.type || 'message'} ${m.serial || ''}`);
      if (selected === m.key) {
        setSelected(null);
        setDetail(null);
      }
      reload();
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`);
    }
  }, [reload, toast, selected]);

  const types       = useMemo(() => Array.from(new Set(messages.map((m) => m.type).filter(Boolean))).sort(), [messages]);
  const originators = useMemo(() => Array.from(new Set(messages.map((m) => m.originator).filter(Boolean))).sort(), [messages]);

  const searched = useMemo(
    () => applySearch(messages, search, [
      'type', 'originator', 'serial', 'key', 'parseStatus',
      (m) => m.exercise || '',
      (m) => m.operation || '',
      (m) => m.qual || '',
    ]),
    [messages, search]
  );

  const { pageRows, sortKey, sortDir, toggleSort, page, totalPages, setPage, total } = useTableState(searched, {
    defaultSortKey: 'receivedAt',
    defaultSortDir: 'desc',
    pageSize: PAGE_SIZE,
    sortAccessors: {
      receivedAt:    (m) => m.receivedAt || '',
      type:          (m) => m.type || '',
      originator:    (m) => m.originator || '',
      serial:        (m) => parseInt(m.serial, 10) || 0,
      parseStatus:   (m) => m.parseStatus || '',
      geometryCount: (m) => m.geometryCount || 0,
      acked:         (m) => acksSummary[m.key]?.acked || 0,
    },
  });

  const counts = useMemo(() => {
    const out = { total: messages.length, ok: 0, partial: 0, failed: 0 };
    for (const m of messages) {
      if (m.parseStatus && out[m.parseStatus] !== undefined) out[m.parseStatus]++;
    }
    return out;
  }, [messages]);

  const exportColumns = useMemo(() => [
    { key: 'receivedAt',     label: 'Received (ISO)' },
    { key: 'type',           label: 'Type' },
    { key: 'serial',         label: 'Serial' },
    { key: 'originator',     label: 'Originator' },
    { key: 'exercise',       label: 'Exercise' },
    { key: 'operation',      label: 'Operation' },
    { key: 'qual',           label: 'Classification' },
    { key: 'periodStartIso', label: 'Period start' },
    { key: 'periodEndIso',   label: 'Period end' },
    { key: 'parseStatus',    label: 'Parse status' },
    { key: 'geometryCount',  label: 'Geometry count' },
    { key: 'amendsKey',      label: 'Amends' },
    { key: 'supersedesKey',  label: 'Supersedes' },
    { key: 'supersededBy',   label: 'Superseded by' },
    { key: 'acked',          label: 'Acked',   get: (m) => acksSummary[m.key]?.acked || 0 },
    { key: 'refused',        label: 'Refused', get: (m) => acksSummary[m.key]?.refused || 0 },
    { key: 'key',            label: 'Message key' },
  ], [acksSummary]);

  return (
    <PageShell
      icon="fa-file-lines"
      title="ADatP-3 Bridge"
      subtitle="NATO MTF messages with map-visible geometry — the source of cop-l-adatp3-* layers. The full inbox (incl. text-only orders/reports) lives on ADatP-3 Orders."
      actions={
        notInstalled ? null : (
          <div className="flex flex-wrap items-center gap-2">
            <LastRefresh ts={refreshedAt} />
            <ExportButtons
              getRows={() => searched}
              columns={exportColumns}
              filenameBase="adatp3-bridge"
              disabled={searched.length === 0}
            />
            {isAdmin && (
              <>
                <button className="btn-secondary" onClick={() => setShowUpload(true)}>
                  <i className="fa-sharp fa-solid fa-upload" /> Upload .txt
                </button>
                <button className="btn-secondary" onClick={() => setShowRouting(true)}>
                  <i className="fa-sharp fa-solid fa-shuffle" /> Manage Routing
                </button>
                <button
                  className="btn-secondary text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={async () => {
                    if (!window.confirm('Delete ALL ADatP-3 messages from inbox? This is irreversible.')) return;
                    try {
                      const r = await api.cop.adatp3.removeAll();
                      toast.success(`Deleted ${r.deleted} messages`);
                      reload();
                    } catch (err) { toast.error(err.message); }
                  }}
                >
                  <i className="fa-sharp fa-solid fa-trash" /> Delete All
                </button>
              </>
            )}
          </div>
        )
      }
    >
      {notInstalled ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" />
          COP ADMS service is not reachable
          {notInstalled.reason ? ` (${notInstalled.reason})` : ''}. Open
          <span className="font-medium"> COP ADMS → Datasets</span> for diagnostics.
        </div>
      ) : loading ? (
        <div className="card p-10 text-center text-sm text-mist">Loading ADatP-3 inbox…</div>
      ) : !status?.enabled ? (
        <section className="card p-5 space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600">
            <i className="fa-sharp fa-solid fa-file-lines" />
            NATO ADatP-3
          </div>
          <p className="text-sm leading-7 text-mist">
            Module disabled. Set <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs">enabled: true</code> in <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs">data/adatp3.json</code> and restart the service.
          </p>
        </section>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="On map"  value={counts.total} />
            <Stat label="OK"      value={counts.ok}      tone="emerald" />
            <Stat label="Partial" value={counts.partial} tone="amber" />
            <Stat label="Failed"  value={counts.failed}  tone="rose" />
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <select className="field !w-44" value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}>
              <option value="">All types</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="field !w-56" value={filter.originator} onChange={(e) => setFilter((f) => ({ ...f, originator: e.target.value }))}>
              <option value="">All originators</option>
              {originators.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select className="field !w-44" value={filter.parseStatus} onChange={(e) => setFilter((f) => ({ ...f, parseStatus: e.target.value }))}>
              <option value="">Any parse status</option>
              <option value="ok">ok</option>
              <option value="partial">partial</option>
              <option value="failed">failed</option>
            </select>
            <div className="min-w-[220px] flex-1 max-w-md">
              <SearchInput value={search} onChange={setSearch} placeholder="Search: type / originator / serial / exercise…" />
            </div>
            <span className="ml-auto text-xs text-mist">{total} {total === 1 ? 'message' : 'messages'}{search || filter.type || filter.originator || filter.parseStatus ? ` (of ${messages.length})` : ''}</span>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="border-b border-line bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-mist">
                <tr>
                  <SortableTh label="Received"   sortKey={sortKey} dir={sortDir} colKey="receivedAt"    onSort={toggleSort} className="px-3 py-2.5 w-32" />
                  <SortableTh label="Type"       sortKey={sortKey} dir={sortDir} colKey="type"          onSort={toggleSort} className="px-3 py-2.5 w-32" />
                  <SortableTh label="Originator" sortKey={sortKey} dir={sortDir} colKey="originator"    onSort={toggleSort} className="px-3 py-2.5" />
                  <SortableTh label="Serial"     sortKey={sortKey} dir={sortDir} colKey="serial"        onSort={toggleSort} className="px-3 py-2.5 w-24 text-right" />
                  <SortableTh label="Status"     sortKey={sortKey} dir={sortDir} colKey="parseStatus"   onSort={toggleSort} className="px-3 py-2.5 w-24" />
                  <SortableTh label="Geom"       sortKey={sortKey} dir={sortDir} colKey="geometryCount" onSort={toggleSort} className="px-3 py-2.5 w-20 text-right" />
                  <SortableTh label="ACKS"       sortKey={sortKey} dir={sortDir} colKey="acked"         onSort={toggleSort} className="px-3 py-2.5 w-20 text-right" />
                  <th className="px-3 py-2.5 w-28 text-right whitespace-nowrap">Refs</th>
                  {isAdmin && <th className="px-3 py-2.5 w-12 text-center"></th>}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 9 : 8} className="p-8 text-center text-mist">
                    {search || filter.type || filter.originator || filter.parseStatus ? 'No messages match the filters / search.' : 'Inbox empty.'}
                  </td></tr>
                ) : pageRows.map((m) => (
                  <tr
                    key={m.key}
                    className={`border-b border-line last:border-0 cursor-pointer hover:bg-slate-50 ${selected === m.key ? 'bg-blue-50/50' : ''}`}
                    onClick={() => openDetail(m.key)}
                  >
                    <td className="px-3 py-2 text-xs text-mist tabular-nums">{m.receivedAt ? formatDate(m.receivedAt) : '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-ink">{m.type || '?'}</td>
                    <td className="px-3 py-2 text-xs">{m.originator || '?'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-mist">{m.serial || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusBadge(m.parseStatus)}`}>{m.parseStatus}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      {m.geometryCount > 0 ? <span className="text-emerald-700">📍 {m.geometryCount}</span> : <span className="text-mist">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      <span title="Acked / Refused">
                        <span className="font-semibold" style={{ color: '#70AF6D' }}>{acksSummary[m.key]?.acked || 0}</span>
                        <span className="text-mist">/</span>
                        <span className="text-rose-700 font-semibold">{acksSummary[m.key]?.refused || 0}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-mist text-right whitespace-nowrap">
                      {m.amendedBy?.length ? <span title="amended">⚠ {m.amendedBy.length}</span> : null}
                      {m.supersededBy ? <span className="ml-1 text-rose-600" title="superseded">🚫</span> : null}
                      {m.referencedBy?.length ? <span className="ml-1" title="referenced by">🔗 {m.referencedBy.length}</span> : null}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={(e) => handleDeleteOne(e, m)}
                          className="text-mist hover:text-rose-600"
                          title="Delete this message (and its derived map objects)"
                        >
                          <i className="fa-sharp fa-solid fa-trash text-xs" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} setPage={setPage} />
          </div>

          {detail && <MessageDetail
            detail={detail}
            tz={tz}
            onClose={() => { setDetail(null); setSelected(null); }}
            isAdmin={isAdmin}
            onReparse={isAdmin ? async () => {
              try {
                await api.cop.adatp3.reparse(selected);
                toast.success('Reparsed');
                await reload();
                await openDetail(selected);
              } catch (err) { toast.error(err.message); }
            } : null}
          />}

          {showUpload  && <UploadModal  toast={toast} onClose={() => setShowUpload(false)}  onUploaded={(r) => { toast.success(`Uploaded — ${r.status}`); setShowUpload(false); reload(); }} />}
          {showRouting && <RoutingModal toast={toast} onClose={() => setShowRouting(false)} onSaved={() => { toast.success('Routing saved'); setShowRouting(false); reload(); }} />}
        </>
      )}
    </PageShell>
  );
}

function SortableTh({ label, sortKey, dir, colKey, onSort, className = '' }) {
  const active = sortKey === colKey;
  return (
    <th className={`${className} cursor-pointer select-none whitespace-nowrap`} onClick={() => onSort(colKey)}>
      <span>{label}</span>
      <SortIcon active={active} dir={dir} />
    </th>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === 'emerald' ? 'text-emerald-700'
            : tone === 'amber'   ? 'text-amber-700'
            : tone === 'rose'    ? 'text-rose-700'
            : 'text-ink';
  return (
    <div className="rounded-2xl border border-line bg-white px-4 py-3 shadow-card">
      <div className="text-xs uppercase tracking-wider text-mist">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function UploadModal({ toast: _toast, onClose, onUploaded }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function handleUpload(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const result = await api.cop.adatp3.upload(text);
      onUploaded(result);
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 backdrop-blur-[2px]" onClick={onClose}>
      <form className="w-full max-w-2xl rounded-[28px] border border-line bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} onSubmit={handleUpload}>
        <h3 className="mb-2 text-lg font-semibold text-ink">Upload ADatP-3 Message</h3>
        <p className="mb-3 text-xs text-mist">Paste a NATO MTF message or load a .txt file. Manual upload bypasses any provider — message is parsed and indexed locally.</p>
        {err && <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-2 text-sm text-red-700">{err}</div>}
        <input type="file" accept=".txt,.eml" onChange={handleFile} className="mb-2 text-xs" />
        <textarea className="field w-full font-mono text-xs" rows={14} value={text} onChange={(e) => setText(e.target.value)} placeholder="MSGID/...//&#10;TIMEZONE/Z//&#10;..." />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy || !text.trim()}>{busy ? 'Uploading…' : 'Upload'}</button>
        </div>
      </form>
    </div>
  );
}

function RoutingModal({ toast: _toast, onClose, onSaved }) {
  const [table, setTable] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.cop.adatp3.getRouting().then(setTable).catch((e) => setErr(e.message)); }, []);
  function update(idx, patch) { setTable((t) => ({ ...t, rules: t.rules.map((r, i) => i === idx ? { ...r, ...patch } : r) })); }
  function remove(idx) { setTable((t) => ({ ...t, rules: t.rules.filter((_, i) => i !== idx) })); }
  function add() { setTable((t) => ({ ...t, rules: [...(t?.rules || []), { match: '', datasetType: 'operational' }] })); }
  async function save(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api.cop.adatp3.updateRouting(table);
      onSaved();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }
  if (!table) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 backdrop-blur-[2px]" onClick={onClose}>
      <form className="w-full max-w-2xl rounded-[28px] border border-line bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <h3 className="mb-1 text-lg font-semibold text-ink">Manage MSGID Routing</h3>
        <p className="mb-3 text-xs text-mist">Match patterns use &apos;*&apos; as wildcard. First match wins; unmatched messages fall back to default.</p>
        {err && <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-2 text-sm text-red-700">{err}</div>}
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="text-mist">Default datasetType:</span>
          <input className="field flex-1 font-mono text-xs" value={table.default} onChange={(e) => setTable((t) => ({ ...t, default: e.target.value }))} />
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="text-mist text-[10px] uppercase">
              <tr><th className="text-left py-1">match</th><th className="text-left py-1">datasetType</th><th className="w-8" /></tr>
            </thead>
            <tbody>
              {table.rules.map((r, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  <td className="py-1 pr-2"><input className="field w-full font-mono" value={r.match} onChange={(e) => update(i, { match: e.target.value })} /></td>
                  <td className="py-1 pr-2"><input className="field w-full font-mono" value={r.datasetType} onChange={(e) => update(i, { datasetType: e.target.value })} /></td>
                  <td className="py-1 text-right"><button type="button" className="text-mist hover:text-rose-600" onClick={() => remove(i)}><i className="fa-sharp fa-solid fa-xmark" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3"><button type="button" className="btn-secondary text-xs" onClick={add}><i className="fa-sharp fa-solid fa-plus mr-1.5" />Add rule</button></div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}
